/**
 * This component will be used on category pages for both sub-category and brand navigation. It includes the Image and Link_Huge components.
 *
 * @module views/components/CategoryCard
 * @memberof -Common
 */
import './CategoryCard.scss';

import React from 'react';

import PropTypes from 'prop-types';

import PaletteNavigationCard from '@ulta/core/adapters/palette/components/PaletteNavigationCard/PaletteNavigationCard';
import Image from '@ulta/core/components/Image/Image';
import Link_Huge from '@ulta/core/components/Link_Huge/Link_Huge';


/**
 * Represents a CategoryCard component
 *
 * @method
 * @param {CategoryCardProps} props - React properties passed from composition
 * @returns CategoryCard
 */
export const CategoryCard = function( props ){
  const {
    action,
    navType,
    image,
    imageMetaData,
    allProductsLabel,
    shopAllLabel,
    label,
    customClassName
  } = props;

  if( !label && !shopAllLabel && !action && !image ){
    return null;
  }

  const externalMediaElement = image ? (
    <Image
      aspectRatio='1:1'
      src={ image }
      metaData={ imageMetaData }
      shouldUseIntersectionObserver={ false }
    />
  ) : undefined;
  const formattedBackgroundColor = PALETTE_COLOR_MAPPING[ navType ];
  const formattedBorderColor = navType === 'brand' ? 'border-xsubtle' : undefined;
  const formattedTextOnlyLabel = !image ? allProductsLabel : undefined;
  const formattedActionLabel = shopAllLabel ? shopAllLabel : label;
  return (
    <div className='CategoryCard'>
      <Link_Huge
        action={ action }
      >
        <PaletteNavigationCard
          externalMediaElement={ externalMediaElement }
          backgroundColor={ formattedBackgroundColor }
          actionLabel={ formattedActionLabel }
          textOnlyLabel={ formattedTextOnlyLabel }
          borderColor={ formattedBorderColor }
          customClassName={ customClassName }
        />
      </Link_Huge>
    </div>
  );
};

/**
 * Mapping of background color tokens to palette background color tokens
 *
 * @type {Object.<string, string>}
 */
const PALETTE_COLOR_MAPPING = {
  'category': 'background-xsubtle',
  'viewAll': 'background-brand-02'
};

/**
 * Property type definitions
 * @typedef CategoryCardProps
 * @type {object}
 * @property {string} navType - Sets the card/nav type (currently one of 'category', 'viewAll', 'brand')
 * @property {string} link - Sets the link location
 * @property {string} label - Sets the label text
 * @property {string} image - Sets the image location
 * @property {string} shopAllLabel - Sets the shop all label
 * @property {string} description - Sets the description
 * @property {string} allProductsLabel - Sets the all products label
 * @property {string} customClassName - customClassName to adjust the container
 */
export const propTypes = {
  /** Sets the card/nav type (currently one of 'category', 'viewAll', 'brand') */
  navType: PropTypes.oneOf( ['category', 'viewAll', 'brand'] ),
  /** Sets the label text */
  label: PropTypes.string,
  /** Sets the image location */
  image: PropTypes.string,
  /** Sets the shop all label */
  shopAllLabel: PropTypes.string,
  /** Sets the description */
  description: PropTypes.string,
  /** Sets the all products label */
  allProductsLabel: PropTypes.string,
  /** Sets the all products action */
  action: PropTypes.object,
  /** customClassName to adjust the container */
  customClassName: PropTypes.string
};



CategoryCard.propTypes = propTypes;

export default CategoryCard;
